import { useTranslation } from 'next-i18next';
import React from 'react';

import { Button, ModalFooter } from 'ui';
import { Flex } from 'ui/flex';
import { GridGap } from 'ui/grid/grid.types';

import css from './edit-moderated-news-modal.module.scss';
import { LoadNewsWidgetLinkType } from '../../types/load-news-widget-link-type';
import { NewsFormDataType } from '../../types/news-form-data-type';
import { UploadType } from '../../types/upload-type';
import EditNewsModal from '../edit-news-modal';

interface EditModeratedNewsModalProps {
  newsFormData: NewsFormDataType;
  loading: boolean;
  isNewsDataModified: boolean;
  myNewsReview: boolean;
  uploadImage: UploadType;
  uploadVideo: UploadType;
  uploadPreviewImage: (img: File) => void;
  uploadPreviewVideo: (img: File) => void;
  loadNewsWidgetLink: LoadNewsWidgetLinkType;
  updateModeratedNews: () => Promise<boolean>;
  closeModal: () => void;
  publishNews: () => void;
  rejectNews: () => void;
  setNewsReview: () => void;
  updateNewsFormData: <T extends keyof NewsFormDataType>(
    field: T,
    value: NewsFormDataType[T]
  ) => void;
}

const EditModeratedNewsModal: React.FC<EditModeratedNewsModalProps> = ({
  newsFormData,
  uploadImage,
  uploadVideo,
  loadNewsWidgetLink,
  closeModal,
  loading,
  isNewsDataModified,
  updateNewsFormData,
  uploadPreviewImage,
  uploadPreviewVideo,
  updateModeratedNews,
  publishNews,
  rejectNews,
  setNewsReview,
  myNewsReview,
}) => {
  const { t } = useTranslation(['common', 'news']);

  return (
    <EditNewsModal
      previewUploadDescription={newsFormData.previewUploadDescription}
      saveNewsPreviewUploadDescription={(previewUploadDescription) =>
        updateNewsFormData('previewUploadDescription', previewUploadDescription)
      }
      newsData={newsFormData}
      uploadNewsImage={uploadImage}
      uploadNewsVideo={uploadVideo}
      loadNewsWidgetLink={loadNewsWidgetLink}
      saveSportId={(sportId) => updateNewsFormData('sportId', sportId)}
      saveCategoryId={(categoryId) =>
        updateNewsFormData('categoryId', categoryId)
      }
      saveNewsTitle={(title) => updateNewsFormData('title', title)}
      saveNewsPreviewDescription={(previewDescription) =>
        updateNewsFormData('previewDescription', previewDescription)
      }
      uploadPreviewImage={uploadPreviewImage}
      uploadPreviewVideo={uploadPreviewVideo}
      myNewsReview={myNewsReview}
      setNewsReview={setNewsReview}
      saveEditorBlocks={(blocks) => updateNewsFormData('blocks', blocks)}
      closeModal={closeModal}
    >
      <ModalFooter className={css.ButtonContainer}>
        <Flex wrap="wrap" gap={GridGap.x1}>
          <Button
            className={css.Button}
            onClick={updateModeratedNews}
            disabled={!isNewsDataModified || loading || !myNewsReview}
          >
            {t('common:actions:save')}
          </Button>

          <Button
            className={css.PublishButton}
            disabled={loading || !myNewsReview}
            onClick={publishNews}
          >
            {t('news:actions:publish')}
          </Button>

          <Button
            color="secondary"
            disabled={loading || !myNewsReview}
            onClick={rejectNews}
          >
            {t('news:actions:reject')}
          </Button>
        </Flex>
      </ModalFooter>
    </EditNewsModal>
  );
};

export default EditModeratedNewsModal;
